// @flow

import * as React from 'react';

import { useNavigate } from 'react-router-dom';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import type { CreateData as CustomerCreateData } from '@gandi/redux-organization';
import type { TranslatorProps } from '@gandi/translate';
import { Base, Button } from '@gandi/ui';

import { renderOption as renderCustomerInfo } from 'src/Cart/AccountChoice/components/CustomerSelect';
import type { CreateCustomerAction } from 'src/Cart/Customer/modules/customers';

import { Layout } from '../../components/Layout';
import type {
  WithRouterProps,
  WithNeedsAuthProps,
  FullState,
} from '../../types';

type Params = {
  cartId: string,
};

export type InjectedProps = {
  translator: TranslatorProps,
  ...WithRouterProps<Params>,
  ...WithNeedsAuthProps,
};

export type StateProps = {
  sharingId: string,
  customerFormValues: CustomerCreateData,
};

export type DispatchProps = {
  createCustomer: CreateCustomerAction<Promise<any>>,
  setCartCustomer: (customerId: string) => void,
  destroyForm: () => mixed,
};

export type Props = {
  ...InjectedProps,
  ...StateProps,
  ...DispatchProps,
};

type ContentProps = {
  translator: InjectedProps['translator'],
  back: string,
  loading: boolean,
  onContinueClick: () => any,
  children: React.Node,
};

function Content({
  children,
  translator,
  back,
  loading,
  onContinueClick,
}: ContentProps) {
  const { __ } = translator;

  const handleContinueClick = (evt: SyntheticMouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    onContinueClick();
  };

  return (
    <Layout currentStep={1}>
      <Base marginBottom="base">{children}</Base>
      <Button
        kind="outline"
        to={loading ? undefined : back}
        loading={loading}
        grouped
      >
        {__('Edit')}
      </Button>
      <Button onClick={handleContinueClick} disabled={loading}>
        {__('Continue')}
      </Button>
    </Layout>
  );
}

function Presenter(props: Props): React.Node {
  const {
    params: { cartId },
    translator,
    translator: { __ },
    customerFormValues,
    sharingId,
    createCustomer,
    setCartCustomer,
    destroyForm,
  } = props;
  const navigate = useNavigate();

  const back = `/cart/${cartId}/configure/customer/create`;
  const next = `/cart/${cartId}/configure/account-choice`;

  const [loading, setLoading] = React.useState(false);

  const onContinueClick = async () => {
    setLoading(true);

    const response = await createCustomer(sharingId, customerFormValues, {
      dryrun: false,
    });
    setCartCustomer(response.data.id);

    navigate(next, { replace: true });
    destroyForm();
  };

  const title = __('Verify customer information');

  return (
    <Content
      translator={translator}
      loading={loading}
      back={back}
      onContinueClick={onContinueClick}
    >
      <h1>{title}</h1>
      <Base backgroundColor="surface">
        {renderCustomerInfo(
          translator.__,
          // $FlowFixMe
          customerFormValues,
        )}
      </Base>
    </Content>
  );
}

Presenter.handleRedirect = function handleRedirect(
  args: StaticHandlerArgs<{}, Params, empty, FullState>,
): Promise<any> {
  const cartId = args.params.cartId;

  const redirectTo = `/cart/${cartId}/configure/account-choice`;
  args.redirect(redirectTo);
  throw new Error('Redirecting to account-choice.');
};

export default Presenter;
